import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AxiosError } from 'axios';
import * as Sentry from '@sentry/react';
import { toast } from 'react-toastify';
import ChatPresentation from 'chat-ui/ChatPresentation';
import useChatStore from 'chat-ui/src/store';
import Cookies from 'js-cookie';
import { isOldToken, useMinimumLoaderTime } from '@common/utils';
import { getErrorMessage, getErrorStatus } from '@common/utils/getErrorDetails';
import { StatusCodes } from 'http-status-codes';
import { iframeLocalStorage } from 'chat-ui/src/utils/iframeLocalStorage';
import ImageViewer from 'chat-ui/src/components/Image/ImageViewer.tsx';
import axiosInstanceChat, { getChatHistory } from '@/axios/axiosInstanceChat';
import FullPageLoader from '../FullPageLoader';
import { useThemeQuery } from '@/reactQuery/get';
import ScreeningQuestionsScreen from './ScreeninQuestionsScreen/ScreeningQuestionsScreen';
import { ChatProps } from '@/types';
import { ChatErrors } from '@/enums/enums';

// const chatCannotRegisterError = `We cannot find this chat.
// Please check the link or contact the owner of the chat.`;
const errorSendingMessage = "We can't send your message. Please try again.";

function Chat({
  projectId,
  chatHeight,
  setProjectConsent,
  screeningQuestions,
  projectStorageSuffix,
}: ChatProps) {
  const {
    addErrorMessage,
    setInitialMessages,
    setSeen,
    setRefreshChatHistory,
    currentConversationId,
    setCurrentConversationId,
    setInputsDisabled,
  } = useChatStore((state) => state);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const minLoaderTime = useMinimumLoaderTime();
  const [searchParams] = useSearchParams();
  const isSandboxChat = searchParams.get('environment');
  const sandboxLink = isSandboxChat === 'sandbox';
  const [areScreeningQuestionsEnded, setAreScreeningQuestionsEnded] = useState(false);
  const { data: themeData } = useThemeQuery(projectId);
  const token = Cookies.get('token');

  const hasQualifiedForInterview =
    iframeLocalStorage.getItem(`screening_state${projectStorageSuffix}`) === 'qualified';
  const showScreeningQuestions =
    screeningQuestions &&
    screeningQuestions?.length > 0 &&
    !areScreeningQuestionsEnded &&
    !hasQualifiedForInterview;
  // eslint-disable-next-line consistent-return
  const refreshChatHistory = useCallback(async () => {
    // Start a Sentry transaction for chat history refresh
    const transaction = Sentry.startTransaction({
      name: 'chat.refresh_history',
      op: 'chat.messaging',
    });

    // Add breadcrumb for chat history refresh attempt
    Sentry.addBreadcrumb({
      category: 'chat.messaging',
      message: 'Refreshing chat history',
      level: 'info',
      data: { conversationId: currentConversationId },
    });

    if (currentConversationId) {
      try {
        const { messages } = await getChatHistory(currentConversationId);
        setInitialMessages(messages);
        setSeen();

        // Add success breadcrumb
        Sentry.addBreadcrumb({
          category: 'chat.messaging',
          message: 'Chat history refreshed successfully',
          level: 'info',
          data: {
            conversationId: currentConversationId,
            messageCount: messages.length,
          },
        });
      } catch (err) {
        const axiosError = err as AxiosError;
        const chatError = axiosError?.response?.status;

        // Enhanced error handling with Sentry
        Sentry.withScope((scope) => {
          scope.setTag('chat_flow', 'refresh_history');
          scope.setTag('error_status', chatError?.toString() || 'unknown');
          scope.setContext('refresh_context', {
            conversationId: currentConversationId,
            errorStatus: chatError,
            errorMessage: axiosError?.response?.data || 'Unknown error',
          });

          // Apply custom fingerprint for refresh history errors
          // Group by status code to differentiate expired chats from other issues
          if (chatError === ChatErrors.CHAT_EXPIRED) {
            scope.setFingerprint(['chat-expired-error']);
          } else if (chatError) {
            scope.setFingerprint(['chat-refresh-error', `status-${chatError}`]);
          } else {
            scope.setFingerprint(['chat-refresh-error', 'unknown-status']);
          }

          Sentry.captureException(err);
        });

        if (chatError === ChatErrors.CHAT_EXPIRED) {
          Sentry.addBreadcrumb({
            category: 'navigation',
            message: 'Chat expired, redirecting to error page',
            level: 'info',
          });
          return navigate(`/chat/error?error_status=${ChatErrors.CHAT_EXPIRED}`);
        }
        return addErrorMessage(errorSendingMessage);
      } finally {
        transaction.finish();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentConversationId]);

  useEffect(() => {
    setRefreshChatHistory(() => refreshChatHistory());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentConversationId]);

  const handleChatRegister = useCallback(
    async () => {
      // Start a Sentry transaction for chat registration
      const transaction = Sentry.startTransaction({
        name: 'chat.register',
        op: 'chat.initialization',
      });

      // Add user context to Sentry for the session
      Sentry.setUser({
        id:
          iframeLocalStorage.getItem(
            `current_conversation_id${sandboxLink ? '/sandbox' : ''}${projectStorageSuffix}`,
          ) || 'unknown',
        project: projectId || 'unknown',
        isSandbox: sandboxLink,
      });

      // Add breadcrumb for chat registration attempt
      Sentry.addBreadcrumb({
        category: 'chat.lifecycle',
        message: 'Attempting to register chat',
        level: 'info',
        data: {
          projectId,
          sandboxLink,
          projectStorageSuffix,
          hasScreeningQuestions: !!screeningQuestions?.length,
        },
      });

      const conversationId = iframeLocalStorage.getItem(
        `current_conversation_id${projectStorageSuffix}`,
      ) as string;

      setLoading(true);
      try {
        setCurrentConversationId(conversationId);

        // Add success breadcrumb
        Sentry.addBreadcrumb({
          category: 'chat.lifecycle',
          message: 'Chat registration successful',
          level: 'info',
          data: { conversationId },
        });
      } catch (error) {
        // Enhanced error handling with Sentry
        Sentry.withScope((scope) => {
          scope.setTag('chat_flow', 'registration');
          scope.setContext('registration_context', {
            projectId,
            sandboxLink,
            conversationId,
            screeningQuestions: !!screeningQuestions?.length,
          });

          // Device/browser information
          scope.setContext('device_info', {
            userAgent: navigator.userAgent,
            language: navigator.language,
            screenSize: `${window.screen.width}x${window.screen.height}`,
            viewportSize: `${window.innerWidth}x${window.innerHeight}`,
            connection: navigator.connection ? navigator.connection.effectiveType : 'unknown',
          });

          // Apply custom fingerprint to group chat registration errors
          // We want all registration errors grouped by sandbox status and
          // whether they happened during screening or after
          scope.setFingerprint([
            'chat-registration-error',
            sandboxLink ? 'sandbox' : 'production',
            screeningQuestions?.length ? 'with-screening' : 'no-screening',
            areScreeningQuestionsEnded ? 'post-screening' : 'pre-screening',
          ]);

          Sentry.captureException(error);
        });

        console.error('Error setting project token:', error);
      } finally {
        setLoading(false);
        transaction.finish();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectId, areScreeningQuestionsEnded],
  );

  useEffect(() => {
    if (!!screeningQuestions?.length && !areScreeningQuestionsEnded) return;
    if (currentConversationId) {
      // Track the initial history fetch attempt
      Sentry.addBreadcrumb({
        category: 'chat.lifecycle',
        message: 'Initial chat history fetch',
        level: 'info',
        data: {
          conversationId: currentConversationId,
          projectId,
          sandboxLink,
          hasScreeningQuestions: !!screeningQuestions?.length,
        },
      });

      getChatHistory(currentConversationId)
        .then((data) => {
          const { messages, reEntered } = data;
          setInitialMessages(messages);
          setSeen();

          if (reEntered > 0) {
            toast.success('Welcome back!');
          }

          // Success breadcrumb
          Sentry.addBreadcrumb({
            category: 'chat.lifecycle',
            message: 'Initial chat history loaded successfully',
            level: 'info',
            data: { messageCount: data.length },
          });
        })
        .catch((error) => {
          const axiosError = error as AxiosError;

          const errorStatus = getErrorStatus(axiosError);
          const message = getErrorMessage(axiosError);

          // Capture error with enhanced context
          Sentry.withScope((scope) => {
            scope.setTag('chat_flow', 'initial_history');
            scope.setTag('error_status', errorStatus.toString());
            scope.setContext('error_details', {
              conversationId: currentConversationId,
              errorData: axiosError?.response?.data,
              errorStatus,
              projectId,
              sandboxLink,
            });

            Sentry.captureException(error);
          });

          if (axiosError && errorStatus !== StatusCodes.UNAUTHORIZED) {
            addErrorMessage(message || errorSendingMessage);
            setInputsDisabled(true);

            Sentry.addBreadcrumb({
              category: 'chat.recovery',
              message: 'Error shown in toast, no reload',
              level: 'warning',
              data: {
                errorStatus,
                handling: 'toast_only',
              },
            });
          }
        });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentConversationId, areScreeningQuestionsEnded, screeningQuestions]);

  const handleRestartChat = useCallback(async () => {
    // Start a Sentry transaction for chat restart
    const transaction = Sentry.startTransaction({
      name: 'chat.restart',
      op: 'chat.reset',
    });

    // Add breadcrumb for restart attempt
    Sentry.addBreadcrumb({
      category: 'chat.lifecycle',
      message: 'Attempting to restart chat',
      level: 'info',
      data: {
        projectId,
        currentConversationId,
        sandboxLink,
      },
    });

    try {
      const conversationId = iframeLocalStorage.getItem(
        `current_conversation_id/${projectId}/sandbox`,
      );
      if (!sandboxLink) {
        // Add breadcrumb for API call
        Sentry.addBreadcrumb({
          category: 'chat.api',
          message: 'Calling reset chat API',
          level: 'info',
          data: {
            url: `${import.meta.env.VITE_CHAT_URL}/chat/reset${projectId}/${conversationId}`,
            method: 'POST',
          },
        });

        const response = await axiosInstanceChat.post(
          `${import.meta.env.VITE_CHAT_URL}/chat/reset${projectId}/${conversationId}`,
          undefined,
        );

        setCurrentConversationId(response.data._id);
        setInitialMessages(response.data.messages);

        // Success breadcrumb for API response
        Sentry.addBreadcrumb({
          category: 'chat.api',
          message: 'Reset chat API call successful',
          level: 'info',
          data: {
            newConversationId: response.data._id,
            messageCount: response.data.messages.length,
          },
        });
      } else {
        // Add breadcrumb for sandbox reset
        Sentry.addBreadcrumb({
          category: 'chat.lifecycle',
          message: 'Resetting sandbox chat via localStorage',
          level: 'info',
        });

        setProjectConsent(false);
        const sandboxKeys = [
          `current_project_token_${projectId}/sandbox`,
          `project_consent${projectId}/sandbox`,
          `chat_settings${projectId}`,
          `current_conversation_id${projectId}/sandbox`,
          `screening_state${projectId}/sandbox`,
          `current_project_language_code${projectId}/sandbox`,
        ];
        sandboxKeys.forEach((key) => iframeLocalStorage.removeItem(key));
      }

      // Overall success breadcrumb
      Sentry.addBreadcrumb({
        category: 'chat.lifecycle',
        message: 'Chat restart successful',
        level: 'info',
      });
    } catch (error) {
      // Capture error with enhanced context
      Sentry.withScope((scope) => {
        scope.setTag('chat_flow', 'restart');
        scope.setContext('restart_context', {
          projectId,
          currentConversationId,
          sandboxLink,
          error: error instanceof Error ? error.message : 'Unknown error',
        });

        Sentry.captureException(error);
      });

      addErrorMessage(errorSendingMessage);
    } finally {
      transaction.finish();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, currentConversationId]);

  useEffect(() => {
    if (areScreeningQuestionsEnded) {
      handleChatRegister();
      return;
    }
    if (currentConversationId) {
      setCurrentConversationId(currentConversationId);
      setLoading(false);
    } else if (!currentConversationId) {
      handleChatRegister();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, areScreeningQuestionsEnded, currentConversationId]);

  useEffect(() => {
    if (isSandboxChat !== null && isSandboxChat !== 'sandbox') {
      navigate('/chat/error-page');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSandboxChat]);

  useEffect(() => {
    if (!token) return;
    const isOldJwtToken = isOldToken(token);
    if (isOldJwtToken) {
      Cookies.remove('token');
    }
  }, [token]);

  if (loading || minLoaderTime) return <FullPageLoader themeData={themeData} />;

  if (showScreeningQuestions) {
    return (
      <ScreeningQuestionsScreen
        themeData={themeData}
        screeningQuestions={screeningQuestions}
        onScreenQuestionsEnd={setAreScreeningQuestionsEnded}
        projectStorageSuffix={projectStorageSuffix}
        setProjectConsent={setProjectConsent}
        projectId={projectId}
        isSandboxChat={sandboxLink}
      />
    );
  }

  return (
    <>
      <ImageViewer />
      <ChatPresentation
        chatHeight={chatHeight}
        isTestChat={false}
        handleRestartChat={handleRestartChat}
        themeData={themeData}
        areScreeningQuestionsEnded={areScreeningQuestionsEnded}
        token={token}
        projectStorageSuffix={projectStorageSuffix}
      />
    </>
  );
}

export default Chat;
